import React, {
    useState,
    useEffect,
} from 'react';
import s from './s.module.less';
import { Button, Descriptions, Form, Modal, Select } from 'antd';
import type { TEngagementQuestion } from 'types/operation';
import commonS from 'styles/common.module.less';
import FileUpload from 'components/form/FileUpload';
import QuillRichText from 'components/QuillRichText';
import {
    engagementTipsAddOrUpdate,
    deleteEngagementTips,
} from 'api/operation/assessment';
import cx from 'classnames';
import { LAYOUT_OPTIONS } from 'constants/textVisit';

type IProps = {
    index: number;
    item: TEngagementQuestion;
    questionCount: number;
    assessmentId: string | number;
    onSuccess: (obj: Partial<TEngagementQuestion>) => void;
};

// 根据 layout value 获取对应的 label
const getLayoutLabel = (value?: string): string => {
    if (!value) return '-';
    const option = LAYOUT_OPTIONS.find((opt) => opt.value === value);
    return option?.label || value;
};

const EngagementQuestionsItem: React.FC<IProps> = (props) => {
    const { item, index, assessmentId, onSuccess } = props;
    const [isEdit, setIsEdit] = useState(false);
    const [form] = Form.useForm<TEngagementQuestion & { active: boolean }>();

    const handleEdit = () => {
        setIsEdit(true);
    };

    const handleCancel = () => {
        setIsEdit(false);
    };

    const handleSave = async () => {
        const formValues = await form.validateFields();
        const params = {
            id: item.id,
            seq: item.seq,
            title: formValues.title,
            description: formValues.description,
            type: 'EngagementQuestions',
            imgUrl: formValues.imgUrl,
            mobileImgUrl: formValues.mobileImgUrl,
            layout: formValues.layout,
            assessmentId,
        };
        const { data } = await engagementTipsAddOrUpdate(params);
        if (!data.error) {
            setIsEdit(false);
            onSuccess(params);
        }
    };

    const handleRemove = async () => {
        if (!item.id) {
            return;
        }
        const { data } = await deleteEngagementTips({
            id: item.id,
        });
        if (!data.error) {
            onSuccess({});
        }
    };

    const renderDescription = () => {
        return (
            <Descriptions
                colon={false}
                title={`Engagement questions ${index + 1}`}
                layout="vertical"
                extra={
                    <>
                        <Button
                            type="text"
                            className={s.removeBtn}
                            onClick={() => {
                                Modal.confirm({
                                    className: s.removeModal,
                                    title: ` RemoveEngagement questions ${
                                        index + 1
                                    }`,
                                    content:
                                        'Do you want to remove this RemoveEngagement questions ?',
                                    okText: 'Confirm',
                                    onOk: () => {
                                        handleRemove();
                                    },
                                });
                            }}
                        >
                            Remove
                        </Button>
                        <Button onClick={() => handleEdit()}>Edit</Button>
                    </>
                }
                column={1}
                size="small"
                contentStyle={{
                    marginBottom: '16px',
                }}
            >
                <Descriptions.Item label="Text"><div dangerouslySetInnerHTML={{ __html: item.title }} /></Descriptions.Item>
                <Descriptions.Item label="Description">
                    {item.description ? (
                        <div dangerouslySetInnerHTML={{ __html: item.description }} />
                    ) : (
                        '-'
                    )}
                </Descriptions.Item>
                <Descriptions.Item label="Image">
                    {item.imgUrl ? (
                        <img
                            src={item.imgUrl}
                            alt={item.title}
                            className={s.cardImg}
                        />
                    ) : (
                        '-'
                    )}
                </Descriptions.Item>
                <Descriptions.Item label="Mobile image">
                    {item.mobileImgUrl ? (
                        <img
                            src={item.mobileImgUrl}
                            alt={item.title}
                            className={s.cardImg}
                        />
                    ) : (
                        '-'
                    )}
                </Descriptions.Item>
                <Descriptions.Item label="Layout">{getLayoutLabel(item.layout)}</Descriptions.Item>
            </Descriptions>
        );
    };

    const renderForm = () => {
        return (
            <div>
                <div className={s.headerBar}>
                    <h4 className={s.subtitle}>
                        {`Engagement questions ${index + 1}`}
                    </h4>
                    <div className={s.buttonWrap}>
                        <Button onClick={() => handleCancel()}>Cancel</Button>
                        <Button
                            onClick={() => handleSave()}
                            type="primary"
                            className={s.ml8}
                        >
                            Save changes
                        </Button>
                    </div>
                </div>
                <p className={s.tips}>
                    <span>* </span>
                    Indicates required fields
                </p>
                <Form
                    form={form}
                    className={cx(commonS.formStyle1, s.formContent)}
                    initialValues={{ active: true }}
                    autoComplete="off"
                    layout="vertical"
                    scrollToFirstError
                    requiredMark={false}
                >
                    <>
                        <Form.Item
                            className={s.rowItem}
                            label={
                                <div className={s.formHeader}>
                                    <div>Text<span className={s.required}>*</span></div>
                                    <div className={`${s.subLabel}`}>{'To indicate which text you want to be styled with the highlight color, wrap it with <b> wrapper. For example: <b>This text will be highlighted.</b>'}</div>
                                </div>
                            }
                            name="title"
                            rules={[
                                {
                                    required: true,
                                    message: 'Text is required',
                                },
                            ]}
                        >
                            <QuillRichText
                                maxLength={500}
                                showCount
                                placeholder="Enter your text here..."
                            />
                        </Form.Item>
                        <Form.Item
                            className={s.rowItem}
                            label={
                                <div className={s.formHeader}>
                                    <div>Description</div>
                                    <div className={`${s.subLabel}`}>Enter additional details or context for this engagement question.</div>
                                </div>
                            }
                            name="description"
                        >
                            <QuillRichText
                                maxLength={1000}
                                showCount
                                placeholder="Enter description here..."
                            />
                        </Form.Item>
                        <Form.Item
                            className={s.rowItem}
                            name="imgUrl"
                            label={
                                <div className={s.formHeader}>
                                    <span>Image</span>
                                    <p className={s.descriptionTips}>
                                        Images taller than 100px will be automatically resized
                                        or cropped. Please keep the height within this limit for
                                        best results.
                                    </p>
                                </div>
                            }
                        >
                            <FileUpload
                                accept="image/png, image/jpeg, image/gif"
                                tips="PNG, JPG, GIF up to 2MB"
                                maxSize={2}
                            />
                        </Form.Item>
                        <Form.Item
                            className={s.rowItem}
                            name="mobileImgUrl"
                            label={
                                <div className={s.formHeader}>
                                    <span>Mobile image</span>
                                    <p className={s.descriptionTips}>
                                        Images taller than 100px will be automatically resized
                                        or cropped. Please keep the height within this limit for
                                        best results.
                                    </p>
                                </div>
                            }
                        >
                            <FileUpload
                                accept="image/png, image/jpeg, image/gif"
                                tips="PNG, JPG, GIF up to 2MB"
                                maxSize={2}
                            />
                        </Form.Item>
                        <Form.Item
                            className={s.rowItem}
                            name="layout"
                            label={
                                <div className={s.formHeader}>
                                    <span>Layout</span>
                                </div>
                            }
                            rules={[{ required: true, message: 'Layout is required' }]}
                        >
                            <Select options={LAYOUT_OPTIONS} placeholder="Select layout" />
                        </Form.Item>
                    </>
                </Form>
            </div>
        );
    };

    useEffect(() => {
        if (item) {
            form.setFieldsValue({
                title: item.title,
                description: item.description,
                imgUrl: item.imgUrl,
                mobileImgUrl: item.mobileImgUrl,
                layout: item.layout,
            });
        } else {
            form.resetFields();
        }
    }, [item, form]);

    return (
        <div className={s.wrap}>
            <div className={s.content}>
                {isEdit ? renderForm() : renderDescription()}
            </div>
        </div>
    );
};

export default EngagementQuestionsItem;
